import { FC, useMemo, useCallback, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Table from '@/components/Tables/Table/Table';
import { StoreProduct } from '@/types/menus.types';
import { useSTKContext } from '@/context/context';
import styles from './ProductsTable.module.css';
import Icon from '@/components/Icons/Icon';
import ProductActiveBundlesModal, {
  ProductActiveBundlesModalState,
} from '../ProductActiveBundlesModal/ProductActiveBundlesModal';
import {
  ActiveBundleStoreGroupsResponse,
  blockedCellKey,
  isBlockableChosenPriceRange,
} from '../deactivateProducts.helper';

interface ProductsTableProps {
  storeProducts: StoreProduct[];
  setPriceDeviationsData: (data: {
    isPriceDeviationsDrawerOpen: boolean;
    variantIds: never[];
    selectedGroupName: string;
    cameFromEditDrawer: boolean;
  }) => void;
  blockedCellKeys?: Set<string>;
  activeBundleStoreGroups?: ActiveBundleStoreGroupsResponse[];
  isActiveBundlesLoading?: boolean;
  isActiveBundlesError?: boolean;
}

interface ProductRow {
  productName: string;
  productDescription: string;
  variantId?: string;
  isProductBlocked?: boolean;
  storeGroupNamesById?: Record<string, string>;
  [key: string]: any; // For dynamic store group columns
}

const ProductsTable: FC<ProductsTableProps> = ({
  storeProducts,
  setPriceDeviationsData,
  blockedCellKeys = new Set<string>(),
  activeBundleStoreGroups = [],
  isActiveBundlesLoading = false,
  isActiveBundlesError = false,
}) => {
  const { t } = useTranslation();
  const { storesAndGroups } = useSTKContext();
  const [activeBundlesModal, setActiveBundlesModal] = useState<ProductActiveBundlesModalState | null>(null);

  const resolveStoreGroupId = useCallback(
    (storeGroupName: string, product: StoreProduct): string | undefined => {
      const fromProduct = product.storeGroups?.find((sg) => sg.storeGroupName === storeGroupName)?.storeGroupId;
      if (fromProduct) {
        return fromProduct;
      }

      if (storesAndGroups?.origin === 'tab-stores-groups') {
        return storesAndGroups.storeGroups?.find((sg) => sg.storeGroup === storeGroupName)?.storeGroupId;
      }

      return undefined;
    },
    [storesAndGroups],
  );

  const handleProductAlertClick = useCallback((row: ProductRow) => {
    if (!row.variantId) {
      return;
    }

    setActiveBundlesModal({
      variantId: row.variantId,
      productName: row.productName,
      productDescription: row.productDescription,
      storeGroupNamesById: row.storeGroupNamesById ?? {},
    });
  }, []);

  // Get all store group names from the user's selection (context)
  const allStoreGroupNames = useMemo(() => {
    if (!storesAndGroups) {
      return [];
    }

    // Extract store group names based on origin
    if (storesAndGroups.origin === 'tab-store') {
      // When selecting individual stores, get unique store group names
      const storeGroupSet = new Set<string>();
      storesAndGroups.stores?.forEach((store) => {
        if (store.storeGroup) {
          storeGroupSet.add(store.storeGroup);
        }
      });
      return Array.from(storeGroupSet);
    }

    if (storesAndGroups.origin === 'tab-stores-groups') {
      // When selecting store groups, get the group names
      return storesAndGroups.storeGroups?.map((sg) => sg.storeGroup) || [];
    }

    return [];
  }, [storesAndGroups]);

  // Get store counts from context (same as main table)
  const getStoreGroupsCount = useCallback(
    (header: string) => {
      let total = 0;
      let count = 0;

      if (!storesAndGroups) {
        return { count: 0, total: 0 };
      }

      if (storesAndGroups.origin === 'tab-store') {
        total = storesAndGroups.countByStoreGroup?.find((sg) => sg.storeGroup === header)?.count || 0;
        count = storesAndGroups?.stores?.filter((store) => store.storeGroup === header).length || 0;
      }

      if (storesAndGroups.origin === 'tab-stores-groups') {
        total = storesAndGroups?.storeGroups?.find((sg) => sg.storeGroup === header)?.storeCount || 0;
        count = storesAndGroups?.storeGroups?.find((sg) => sg.storeGroup === header)?.stores.length || 0;
      }

      return { count: count || 0, total: total || 0 };
    },
    [storesAndGroups],
  );

  // Build table data - just use the data directly from products
  const tableData = useMemo(() => {
    if (!storeProducts || storeProducts.length === 0) {
      return [];
    }

    return storeProducts.map((product) => {
      const storeGroupNamesById: Record<string, string> = {};
      product.storeGroups?.forEach((sg) => {
        const resolvedId = sg.storeGroupId ?? resolveStoreGroupId(sg.storeGroupName, product);
        if (resolvedId && sg.storeGroupName) {
          storeGroupNamesById[String(resolvedId).toLowerCase()] = sg.storeGroupName;
        }
      });

      const row: ProductRow = {
        productName: product.productName || '',
        productDescription: product.productDescription || '',
        variantId: product.variantId,
        isProductBlocked: false,
        storeGroupNamesById,
      };

      // For each store group name, get the price from the product's storeGroups array
      allStoreGroupNames.forEach((storeGroupName) => {
        const storeGroupData = product.storeGroups?.find((sg) => sg.storeGroupName === storeGroupName);
        const storeGroupId = storeGroupData?.storeGroupId ?? resolveStoreGroupId(storeGroupName, product);
        const isBlockedByActiveBundles =
          !!product.variantId &&
          !!storeGroupId &&
          blockedCellKeys.has(blockedCellKey(String(product.variantId), String(storeGroupId)));
        const isBlocked = isBlockedByActiveBundles && isBlockableChosenPriceRange(storeGroupData?.chosenPriceRange);

        if (isBlocked) {
          row.isProductBlocked = true;
        }

        row[storeGroupName] = t('common.inactive');
      });

      return row;
    });
  }, [storeProducts, allStoreGroupNames, t, resolveStoreGroupId, blockedCellKeys]);

  // Build columns - one for product name, one for each store group
  const columns = useMemo(() => {
    const cols: any[] = [
      {
        id: 'productName',
        accessorKey: 'productName',
        header: () => <div className={styles.productHeader}>PRODUCT</div>,
        cell: (info: any) => {
          const row = info.row.original as ProductRow;

          return (
            <div className={styles.productCell}>
              {row.isProductBlocked && (
                <span data-testid="blocked-alert-icon" className={styles.productAlertIcon}>
                  <Icon
                    iconName="AlertCircle"
                    size={18}
                    className={styles.blockedAlertIcon}
                    style={{ color: 'var(--red-700)', stroke: 'var(--red-700)' }}
                    onClick={(event) => {
                      event.stopPropagation();
                      handleProductAlertClick(row);
                    }}
                  />
                </span>
              )}
              <div className={styles.productText}>
                <div className={styles.productName}>{info.getValue()}</div>
                <div className={styles.productDescription}>{row.productDescription}</div>
              </div>
            </div>
          );
        },
        size: 350,
        minSize: 250,
        maxSize: 450,
      },
    ];

    // Add a column for each store group
    allStoreGroupNames.forEach((storeGroupName, index) => {
      const { count, total } = getStoreGroupsCount(storeGroupName);

      cols.push({
        id: `${storeGroupName}_${index}`,
        accessorKey: storeGroupName,
        header: () => (
          <div className={styles.headerWithCount}>
            <div className={styles.headerName} title={storeGroupName}>
              {storeGroupName.toUpperCase()}
            </div>
            <div className={styles.headerCount}>
              {count} / {total}
            </div>
          </div>
        ),
        size: 145,
        minSize: 130,
        maxSize: 160,
        cell: (info: any) => {
          const cellData = info.getValue();

          const renderInactiveCell = (label: string) => (
            <div className={styles.priceWithIcon}>
              <span className={styles.inactive}>{label}</span>
            </div>
          );

          // If it's a string (just "Inactive"), display it
          if (typeof cellData === 'string') {
            return renderInactiveCell(cellData);
          }

          // If it's null or undefined, show "Inactive"
          if (!cellData) {
            return renderInactiveCell(t('common.inactive'));
          }

          // If it has a range, show with info icon
          if (cellData.hasRange) {
            return (
              <div className={styles.priceWithIcon}>
                <span className={styles.priceText}>{cellData.display}</span>
                <InfoOutlinedIcon
                  className={styles.infoIcon}
                  onClick={() => {
                    if (cellData.variantId && cellData.groupName) {
                      setPriceDeviationsData({
                        selectedGroupName: cellData.groupName,
                        variantIds: [cellData.variantId] as never[],
                        isPriceDeviationsDrawerOpen: true,
                        cameFromEditDrawer: true,
                      });
                    }
                  }}
                  sx={{
                    width: '18px',
                    height: '18px',
                    color: '#266DCC',
                    cursor: 'pointer',
                    marginLeft: '4px',
                  }}
                />
              </div>
            );
          }

          // Single price - just display it
          return (
            <div className={styles.priceWithIcon}>
              <span className={styles.priceText}>{cellData.display}</span>
            </div>
          );
        },
      });
    });

    return cols;
  }, [allStoreGroupNames, getStoreGroupsCount, setPriceDeviationsData, t, blockedCellKeys, handleProductAlertClick]);

  const hasManyStoreGroups = allStoreGroupNames.length >= 5;

  return (
    <div className={styles.container}>
      <div className={classNames(styles.tableWrapper, { [styles.manyStoreGroups]: hasManyStoreGroups })}>
        <Table
          data={tableData}
          columns={columns}
          includeMinWidth={false}
          height="calc(68px + (76px * 3))"
          className={styles.tableScroll}
        />
      </div>
      <ProductActiveBundlesModal
        modalState={activeBundlesModal}
        activeBundleStoreGroups={activeBundleStoreGroups}
        isLoading={isActiveBundlesLoading}
        isError={isActiveBundlesError}
        onClose={() => setActiveBundlesModal(null)}
      />
    </div>
  );
};

export default ProductsTable;
